import {useInfiniteQuery} from '@tanstack/react-query'
import {startOfDay} from 'date-fns'
import sub from 'date-fns/sub'
import moment from 'moment'
import {useEffect, useMemo, useState} from 'react'
import api, {Alarm, EventRelationsType, FilterEventsType, SearchEventsType} from 'src/services/api'
import {QueryKeyEnum} from 'src/utilities/enums/QueryKeyEnum'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'
import {ListObjectIdEnum} from '../../../pages/alarms/AlarmsFunctions'
import {useQueryParams} from '../../useQuery'
import useWebsocket from '../socket/useWebsocket'

const BATCH_SIZE = 20

function hasActiveEventsSearch(search?: SearchEventsType, params?: URLSearchParams) {
  if (search?.id != null) return true
  if (search?.cameraName?.trim()) return true
  if (search?.clientName?.trim()) return true
  if (params?.get('search')?.trim()) return true
  if (params?.get('camera')?.trim()) return true
  if (params?.get('id')?.trim()) return true
  return false
}

type Props = {
  filters?: FilterEventsType
  search?: SearchEventsType
  labelSelectedOnCalendar?: string
}

export default function useEvents({filters, search, labelSelectedOnCalendar}: Props) {
  const [events, setEvents] = useState<Alarm[]>([])
  const params = useQueryParams()
  const isSearchActive = useMemo(() => hasActiveEventsSearch(search, params), [search, params])

  useEffect(() => {
    if (isSearchActive) setEvents([])
  }, [isSearchActive])

  const onMessage = (event: Alarm) => {
    setEvents((prev) => {
      const newEvents = [...prev]
      if (filters?.statuses?.length) {
        if (filters?.statuses.includes(event.status.description)) newEvents.push(event)
      }
      if (filters?.eventTypeIds?.length) {
        if (filters?.eventTypeIds.includes(String(event.eventTypeId))) newEvents.push(event)
      }
      if (!filters?.statuses?.length) newEvents.push(event)

      const newDetections = newEvents
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))
        .filter((detection) => filters?.companyIds?.includes(detection.companyId))

      return newDetections
    })
  }

  const websocket = useWebsocket({
    room: WebSocketRoomTypeEnum.EVENTS,
    listenTo: [FaceDetectionWSChannelEnum.EVENTS],
    onMessage,
    enable: !isSearchActive,
  })

  const handleUpdateTimeFilterWhenFiltersChange = () => {
    const valuesOnRuler = [
      {updateTime: [moment(sub(new Date(), {minutes: 15})).format(), moment(new Date()).format()], label: '15min'},
      {updateTime: [moment(sub(new Date(), {minutes: 30})).format(), moment(new Date()).format()], label: '30min'},
      {updateTime: [moment(sub(new Date(), {hours: 1})).format(), moment(new Date()).format()], label: '1h'},
      {updateTime: [moment(sub(new Date(), {hours: 6})).format(), moment(new Date()).format()], label: '6h'},
      {updateTime: [moment(sub(new Date(), {hours: 12})).format(), moment(new Date()).format()], label: '12h'},
      {updateTime: [moment(startOfDay(new Date())).format(), moment(new Date()).format()], label: 'Hoje'},
    ]
    return valuesOnRuler.find((it) => it.label === labelSelectedOnCalendar)?.updateTime
  }

  const query = useInfiniteQuery({
    retry: 0,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    queryKey: [QueryKeyEnum.EVENTS, filters, search],
    queryFn: async ({pageParam: nextId}) => {
      if (nextId === -1) throw new Error('No more pages to fetch')

      const includes: Array<EventRelationsType> = ['camera', 'status', 'snapshots', 'snapshots.demarcatedAreas', 'activatedActions']
      filters?.eventTypeIds?.forEach((value) => {
        if (ListObjectIdEnum.LINE_CROSSING === value) includes.push('lineCrossingLogs')
      })

      const response = await api.events.getMany({
        batchSize: BATCH_SIZE,
        startId: nextId === 0 ? null : nextId,
        search,
        filter: filters,
        orderBy: {ids: 'DESC'},
        includes: includes,
      })

      return response
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage && allPages && allPages.some((c) => c && c.data.data.items.some((detection) => detection.id === lastPage.data.data.nextId))) {
        return null
      }

      return lastPage ? lastPage.data.data.nextId : undefined
    },
    throwOnError: () => {
      return false
    },
  })

  const getEventData = (events: Alarm[]) => {
    return events
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((event, index, self) => index === self.findIndex((t) => t.id === event.id))
  }

  const disconnectWebsocket = () => {
    websocket.forceDisconnect()
    setEvents([])
  }

  return {
    ...query,
    webSocketConnectionStatus: websocket?.webSocketConnectionStatus,
    forceConnect: websocket.forceConnect,
    forceDisconnect: disconnectWebsocket,
    data: getEventData([...(isSearchActive ? [] : events), ...(query.data?.pages.flatMap((page) => page.data.data.items) ?? [])]),
    events,
    handleUpdateTimeFilterWhenFiltersChange,
  }
}
